'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import PageType from '@alltrails/amplitude/enums/PageType';
import logExploreMoreTrailsClicked from '@alltrails/amplitude/events/logExploreMoreTrailsClicked';
import logShowMoreClicked from '@alltrails/amplitude/events/logShowMoreClicked';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import logError from '@alltrails/core/utils/logError';
import Button from '@alltrails/denali/components/Button';
import { useGetLocationDataMutation } from '@alltrails/explore-service/redux/exploreServiceApi';
import type ExploreServiceFilters from '@alltrails/explore-service/types/ExploreServiceFilters';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import { LOCATION_SECTION_ID_TOP_TRAILS } from '@/constants/locationSectionIds';
import { FilterConfig, LocationDetailsResponseType } from '@/types/locationDetailsPages/shared';
import { getAnalyticsCardLocation } from '@/utils/getAnalyticsCardLocation';
import { getAnalyticsPageType } from '@/utils/getAnalyticsPageType';
import { getFilters, getLocationId, getPlaceType } from '@/utils/locationDataUtil';
import { useAppSelector } from '@/utils/redux';
import { useLocationMap } from '../LocationMap/LocationMapContext';
import styles from './TopResults.module.scss';
import TopTrailsCard from './TopTrailsCard';

type Props = {
  exploreLink?: string;
  type?: LocationDetailsResponseType;
  totalTrails: number;
  filterConfig: FilterConfig;
  pageType?: PageType;
};

const TopTrails = ({ exploreLink, type, totalTrails, filterConfig }: Props) => {
  const pageType = getAnalyticsPageType(type);
  const reduxListsAnalyticsInfo = useMemo(
    () => ({
      page: PageStrings.SEO_PAGE,
      detailedCardLocation: getAnalyticsCardLocation(type)
    }),
    [type]
  );
  const { isFavorite, handleFavoriteClick, listModal, signUpModal } = useReduxListItems(undefined, undefined, reduxListsAnalyticsInfo);
  const { currentPageForTrailsQuery, handleGetMoreTrails, handleSetTrailsToRender, trailsToRender } = useLocationMap();
  const intl = useIntl();
  const [getLocationData] = useGetLocationDataMutation();
  const isMobileBrowser = useAppSelector((state: { context: { mobileBrowser: boolean } }) => state.context?.mobileBrowser);
  const isTabletOrMobile = useMediaQuery(`(max-width: 1120px)`, isMobileBrowser);
  const [isShowMoreClicked, setIsShowMoreClicked] = useState<boolean>(false);
  const [firstNewCardIndex, setFirstNewCardIndex] = useState<number | null>(null);
  const firstNewCardRef = useRef<HTMLDivElement>(null);

  const handleClickExploreMore = useCallback(() => {
    if (pageType) {
      logExploreMoreTrailsClicked({
        page_type: pageType
      });
    }
  }, [pageType]);

  const onShowMoreClick = useCallback(async () => {
    if (pageType) logShowMoreClicked({ page_type: pageType, show_more_location: 'top trails' });
    setIsShowMoreClicked(true);
    setFirstNewCardIndex(trailsToRender.length);
    const placeType = getPlaceType(filterConfig);
    const locationId = getLocationId(filterConfig);
    const locationIdNumber = locationId ? Number(locationId) : undefined;
    const features = getFilters(filterConfig, 'features');
    const activities = getFilters(filterConfig, 'activities');
    const suitability = getFilters(filterConfig, 'access');
    const difficulty = getFilters(filterConfig, 'difficulty');
    const length = getFilters(filterConfig, 'lengths');

    if (placeType && locationIdNumber && !isNaN(locationIdNumber)) {
      const filters: ExploreServiceFilters = {};
      // Any casts to go from string[] to the more specific string[] types w/o a big refactor right now
      if (activities) filters.activity = activities as any;
      if (features) filters.feature = features as any;
      if (suitability) filters.suitability = suitability as any;
      if (difficulty) filters.difficulty = difficulty as any;
      if (length) filters.length = length as any;

      try {
        const response = await getLocationData({
          filters,
          limit: 10,
          location: { id: locationIdNumber, placeType },
          page: currentPageForTrailsQuery,
          sort: 'most_popular',
          recordAttributesToRetrieve: [
            '_geoloc',
            '_cluster_geoloc',
            'area_id',
            'area_name',
            'area_type',
            'avg_rating',
            'num_reviews',
            'difficulty_rating',
            'duration_minutes',
            'ID',
            'lat',
            'length',
            'lng',
            'name',
            'objectID',
            'type',
            'verified_map_id',
            'slug',
            'description',
            'estimated_time_to_complete'
          ]
        }).unwrap();
        handleSetTrailsToRender(response.searchResults);
        handleGetMoreTrails();
      } catch (error) {
        logError(new Error(`Error fetching show more location data`, { cause: error }));
        setIsShowMoreClicked(false);
        setFirstNewCardIndex(null);
      }
    }
  }, [pageType, filterConfig, getLocationData, currentPageForTrailsQuery, handleSetTrailsToRender, handleGetMoreTrails, trailsToRender.length]);

  useEffect(() => {
    if (firstNewCardIndex !== null && trailsToRender.length > firstNewCardIndex && firstNewCardRef.current) {
      firstNewCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      firstNewCardRef.current.focus();
    }
  }, [firstNewCardIndex, trailsToRender.length]);

  const formatttedMaxTrails = <FormattedNumber value={trailsToRender?.length} />;
  const formattedTotalTrails = <FormattedNumber value={totalTrails} />;

  let displayShowMoreButton = totalTrails > 10 && trailsToRender?.length < totalTrails && type !== 'poi';

  return (
    <div id={LOCATION_SECTION_ID_TOP_TRAILS}>
      <ol className={styles.topResultsContainer}>
        {trailsToRender.map((trail, idx) => {
          const isFirstNewCard = firstNewCardIndex !== null && idx === firstNewCardIndex;
          return (
            <li key={trail.ID} className={styles.topResultListItem}>
              <TopTrailsCard
                ref={isFirstNewCard ? firstNewCardRef : null}
                trail={trail}
                ranking={idx + 1}
                isFavorite={isFavorite('trail', trail.ID)}
                onFavoriteClick={handleFavoriteClick}
                isShowMoreClicked={isShowMoreClicked}
                isTabletOrMobile={isTabletOrMobile}
                focusable={isFirstNewCard}
              />
            </li>
          );
        })}
      </ol>
      {displayShowMoreButton && (
        <div className={styles.showMoreSection}>
          <Button
            className={styles.showMoreButton}
            text={intl.formatMessage({ defaultMessage: 'Show more' })}
            variant="primary"
            testId="show-more-button"
            onClick={onShowMoreClick}
          />
          <div className={styles.showing}>
            <FormattedMessage
              defaultMessage="Showing results {min}–{max} of {total}"
              values={{ min: 1, max: formatttedMaxTrails, total: formattedTotalTrails }}
            />
          </div>
        </div>
      )}
      {exploreLink && !isTabletOrMobile && (
        <Button
          className={styles.exploreMore}
          text={intl.formatMessage({ defaultMessage: 'Explore more trails' })}
          testId="trail-list-explore-more"
          linkInfo={{ href: exploreLink }}
          onClick={handleClickExploreMore}
        />
      )}
      {listModal}
      {signUpModal}
    </div>
  );
};

export default TopTrails;
